import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/* ================= CONFIG ================= */
const API = process.env.REACT_APP_API_URL || "/api";

/* ================= SAFE HELPERS ================= */
const getToken = () => localStorage.getItem("token");

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

function Companies() {
  const navigate = useNavigate();

  const [companies,  setCompanies]  = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const token = getToken();
  const user  = getUser();

  const isSuperAdmin    = user?.role === "super_admin";
  const isSystemManager = user?.role === "system_manager";
  const canManage       = isSuperAdmin || isSystemManager;

  /* ================= FETCH ================= */
  useEffect(() => {
    // ✅ Defined inside effect — no lint warning, always fresh
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error("Failed to fetch companies");

        const data = await res.json();
        const safeData = Array.isArray(data)
          ? data
          : data.rows || data.data || [];

        const normalized = safeData.map((c) => ({
          ...c,
          device_count: Number(c.device_count || 0),
        }));

        setCompanies(normalized);
        setFiltered(normalized);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchCompanies();
  }, [token]);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const result = companies.filter((c) =>
      c.company_name?.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(result);
  }, [search, companies]);

  /* ================= DELETE ================= */
  const handleDelete = async (e, companyId) => {
    e.stopPropagation();

    if (
      !window.confirm(
        "Delete this company and all its users? This cannot be undone."
      )
    )
      return;

    try {
      setDeletingId(companyId);

      const res = await fetch(`${API}/companies/${companyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Delete failed");

      // Optimistic — remove from local state without refetch
      setCompanies((prev) => prev.filter((c) => c.id !== companyId));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  /* ================= RENDER ================= */
  if (loading) return <div className="p-6 text-slate-400">Loading...</div>;
  if (error)   return <div className="p-6 text-red-500">{error}</div>;

  const totalDevices = companies.reduce((a, c) => a + c.device_count, 0);

  return (
    <div className="p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Companies</h2>
          <p className="text-sm text-slate-500">Manage all organizations</p>
        </div>

        {isSuperAdmin && (
          <button
            onClick={() => navigate("/dashboard/companies/add")}
            className="bg-indigo-600 text-white px-5 py-2 rounded-xl hover:bg-indigo-700"
          >
            + Add Company
          </button>
        )}
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total"         value={companies.length} />
        <StatCard title="Active"        value={companies.filter((c) => c.status === "active").length} />
        <StatCard title="Inactive"      value={companies.filter((c) => c.status !== "active").length} />
        <StatCard title="Total Devices" value={totalDevices} />
      </div>

      {/* SEARCH */}
      <div className="bg-white p-4 rounded-xl border">
        <input
          type="text"
          placeholder="Search companies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-2xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-6 py-3 text-left">Company</th>
              <th className="px-6 py-3 text-left">Industry</th>
              <th className="px-6 py-3 text-left">Devices</th>
              <th className="px-6 py-3 text-left">Status</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((company) => (
              <tr
                key={company.id}
                onClick={() =>
                  navigate(`/dashboard/companies/${company.id}`)
                }
                className="hover:bg-slate-50 cursor-pointer border-t transition"
              >
                <td className="px-6 py-4 font-semibold">
                  <div className="flex items-center gap-2">
                    {company.logo_data && <img src={company.logo_data} alt="" className="h-7 w-7 rounded border border-slate-200 object-contain p-0.5" />}
                    {company.company_name}
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-500">
                  {company.company_type || "-"}
                </td>
                <td className="px-6 py-4">{company.device_count}</td>
                <td className="px-6 py-4">
                  <StatusBadge status={company.status} />
                </td>

                {/* Stop row click from firing when clicking buttons */}
                <td
                  className="px-6 py-4 text-right space-x-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Edit — super_admin + system_manager */}
                  {canManage && (
                    <button
                      onClick={() =>
                        navigate(`/dashboard/companies/edit/${company.id}`)
                      }
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs"
                    >
                      Edit
                    </button>
                  )}

                  {/* Delete — super_admin only */}
                  {isSuperAdmin && (
                    <button
                      onClick={(e) => handleDelete(e, company.id)}
                      disabled={deletingId === company.id}
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg text-xs disabled:opacity-50"
                    >
                      {deletingId === company.id ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-10 text-gray-400">
            No companies found
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= SUB-COMPONENTS ================= */

const StatCard = ({ title, value }) => (
  <div className="bg-white p-4 rounded-xl border">
    <p className="text-xs text-gray-400">{title}</p>
    <h2 className="text-xl font-bold mt-1">{value}</h2>
  </div>
);

const StatusBadge = ({ status }) => {
  const isActive = status === "active";
  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-700"
          : "bg-red-100 text-red-600"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

export default Companies;
